import { useEffect, useState } from "react";
import useAuth from "../context/AuthProvider";
import { apiClient } from "../helper/api";
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Tooltip,
} from "@mui/material";
import { Close, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { RiExpandUpDownLine } from "react-icons/ri";
import Swal from "sweetalert2";
import { AddEditPartnersFeesT } from "../components/AddEdit/AddEditPartnersFeesT";
import { PartnersFeesTItemType } from "../models/PartnersFeesTModel";
import { getRowsPerPageOptions } from "../components/AddEdit/CustomFunctions";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Autocomplete } from "@mui/material";
import TextField from "@mui/material/TextField";

enum PartnerFeesTEditDialog {
  closed = 0,
  edit = 1,
  bulkAdd = 2
}

/** Build API filter model: columnField, operatorValue, value, linkOperator (backend format) */
function buildApiFilterModel(filterModel: GridFilterModel | null | undefined): { items: { columnField: string; value: string; operatorValue: string }[]; linkOperator?: 'and' | 'or' } | null {
  if (!filterModel?.items?.length) return null;
  const items = filterModel.items.map((item) => ({
    columnField: item.columnField,
    value: item.value != null ? String(item.value) : '',
    operatorValue: item.operatorValue ?? '',
  }));
  const linkOp = filterModel.linkOperator?.toString().toLowerCase();
  const linkOperator = (linkOp === 'or' ? 'or' : 'and') as 'and' | 'or';
  return { items, linkOperator };
}

// Filter operators that match backend; same set for string and numeric (like state/client)
const STRING_FILTER_OPS = ['contains', 'equals', 'startsWith', 'endsWith', 'isEmpty', 'isNotEmpty', 'isAnyOf'];

const stringFilterOperators = getGridStringOperators().filter((op) =>
  STRING_FILTER_OPS.includes(op.value)
);
// Use same operators for numeric columns as state/client (contains, equals, etc.)
const filterOperators = stringFilterOperators;

export const PartnerFeesT = () => {
  const { user } = useAuth();
  const token = user?.token;
  const [loading, setloading] = useState(false);

  const [listData, setListData] = useState<PartnersFeesTItemType[]>([])

  const initState = { stateId: 0, stateName: "", postalAbbv: '' };
  const [stateList, setStateList] = useState([initState]);

  const initialCustomerList = { partnersId: 0, customerName: "" };
  const [customerList, setCustomerList] = useState([initialCustomerList]);

  const [selState, setSelState] = useState('');
  const [selCustomer, setSelCustomer] = useState('');
  const [isLatest, setIsLatest] = useState<boolean>(false)
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const fetchCustomer = () => {
    apiClient("/Partners/getPartners", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return
        setCustomerList(responseJson.data);
      })
  };
  const fetchStateData = () => {
    apiClient("/States/getStates", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return
        setStateList(responseJson.data);
      })
  };

  useEffect(() => {
    fetchCustomer();
    fetchStateData();
  }, [])

  useEffect(() => {
    fetchPartnersFeesItem(selState, selCustomer, isLatest, filterModel, currentPage, pageSize, sortModel)
  }, [selState, selCustomer, isLatest, filterModel, currentPage, pageSize, sortModel])

  const handleHomeStateChange = (e: SelectChangeEvent<string>) => {
    setSelState(e.target.value);
  };

  const handlePartnerChange = (e: SelectChangeEvent<string>) => {
    const selectedValue = e.target.value;
    setSelCustomer(selectedValue);
    setSelItem((v: PartnersFeesTItemType) => ({
      ...v,
      partnerId: Number(selectedValue),
    }));
  };

  const handleClearFilters = () => {
    setSelState("");
    setSelCustomer("");
    setIsLatest(false);
    setCurrentPage(0);
    setFilterModel({ items: [] });
    setSelItem({ ...initialItem });
  };

  const handleIsLatestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLatest(v => !v)
  }

  const fetchPartnersFeesItem = async (
    stateId: string,
    partnerId: string,
    isLatest: boolean,
    filterModel?: GridFilterModel,
    page: number = 0,
    pageSizeVal: number = 25,
    sortModelVal?: GridSortModel
  ) => {
    setloading(true)
    const sortField = sortModelVal?.length ? sortModelVal[0].field : ''
    const sortDirection = sortModelVal?.length ? sortModelVal[0].sort || 'asc' : 'asc'
    const res = await apiClient("/PartnerTaxandFee/GetPartnerFeesTItem", {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      }),
      body: JSON.stringify({
        stateId: stateId || 0,
        partnersId: partnerId || 0,
        isLatest,
        filterModel: buildApiFilterModel(filterModel) ?? null,
        pageNumber: page + 1,
        pageSize: pageSizeVal,
        field: sortField,
        sort: sortDirection,
      }),
    }).finally(() => {
      setloading(false)
    })
    if (!res?.data) {
      setListData([])
      setTotalRowCount(0)
      return
    }
    // API may put summary row in data (TotalCount, PageSize, PageNumber, TotalPages); use it for total and exclude from rows
    const rawData = Array.isArray(res.data) ? res.data : []
    const summaryRow = rawData.find((r: any) => r.TotalCount != null && (r.partnersFeesTId == null && r.PartnersFeesTId == null))
    let dataRows = rawData.filter((r: any) => r.partnersFeesTId != null || r.PartnersFeesTId != null)
    const total = res.totalCount ?? res.totalRecords ?? summaryRow?.TotalCount ?? dataRows.length ?? 0

    // Client-side sort by customerName when user requested it (API doesn't support it)
    if (sortField === "customerName" && dataRows.length > 0) {
      const mult = sortDirection === "desc" ? -1 : 1
      dataRows = [...dataRows].sort((a: any, b: any) => {
        const na = (a.customerName ?? "").toString().toLowerCase()
        const nb = (b.customerName ?? "").toString().toLowerCase()
        return mult * na.localeCompare(nb)
      })
    }

    setTotalRowCount(total)
    setListData(dataRows.map((v: any, index: number) => ({
      ...v,
      rowNum: index + 1,
      zeroFee: Number(v.zeroFee),
      premiumFee: Number(v.premiumFee),
      policyFee: Number(v.policyFee),
      loydsFee: Number(v.loydsFee),
      binderFee: Number(v.binderFee),
      endorsementFee: Number(v.endorsementFee),
      largeReportFee: Number(v.largeReportFee),
      diligentSearchFee: Number(v.diligentSearchFee),
    })))
  }

  const updateItem = async (stateId: string, selCustomer: string, isLatest: boolean = true) => {
    const data = {}
    return await apiClient("/PartnerTaxandFee/PartnersFeesT_SaveOrUpdate", {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(data)
    })
  }

  const handleAddItem = async () => {
    await updateItem(selState, selCustomer, isLatest).then((responseJson) => {
      if (!responseJson) return
    })
    fetchPartnersFeesItem(selState, selCustomer, isLatest, filterModel, currentPage, pageSize, sortModel);
  }

  const handleDeleteItem = (partnersFeesTId: number | string) => {
    Swal.fire({
      title: 'Are you sure, you want to delete this Client Fee Schedule?',
      text: "You won't be able to revert this!",
      iconHtml: '<svg width="70" height="70" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="31" cy="31" r="31" fill="#02A4E3"/><path d="M31.0003 25.6667V31M31.0003 36.3334H31.0137M44.3337 31C44.3337 38.3638 38.3641 44.3334 31.0003 44.3334C23.6365 44.3334 17.667 38.3638 17.667 31C17.667 23.6362 23.6365 17.6667 31.0003 17.6667C38.3641 17.6667 44.3337 23.6362 44.3337 31Z" stroke="white" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      showCancelButton: true,
      confirmButtonColor: '#02A4E3',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Delete',
      customClass: {
        confirmButton: 'btn-radius',
        cancelButton: 'btn-radius',
        popup: 'card-radius'
      },
      focusConfirm: true,
      allowEnterKey: true,
      stopKeydownPropagation: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.getConfirmButton()?.focus();
      },
    }).then((result) => {
      if (result.isConfirmed) {
        apiClient(`/PartnerTaxandFee/PartnersFeesT_Delete?partnersFeesTId=${partnersFeesTId}`, {
          method: "DELETE",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
        }).then((responseJson) => {
          if (!responseJson) return
          const success = responseJson.status === true || responseJson.resp === 0
          if (success) {
            Swal.fire({
              title: 'Success',
              text: responseJson.message || 'Partner fee soft deleted successfully',
              icon: 'success',
            })
            fetchPartnersFeesItem(selState, selCustomer, isLatest, filterModel, currentPage, pageSize, sortModel);
          } else {
            Swal.fire({
              title: 'Error',
              text: responseJson.message || 'Failed to delete',
              icon: 'error',
            })
          }
        })
      }
    }).finally(() => {
      setloading(false);
    })
  }

  const [isOpen, setIsOpen] = useState<PartnerFeesTEditDialog>(PartnerFeesTEditDialog.closed);
  const initialItem: PartnersFeesTItemType = {
    partnersFeesTId: 0,
    stateId: 0,
    homeState: "",
    partnerId: 0,
    customerName: "",
    zeroFee: 0,
    premiumFee: 0,
    policyFee: 0,
    loydsFee: 0,
    binderFee: 0,
    endorsementFee: 0,
    largeReportFee: 0,
    diligentSearchFee: 0,
    isLatest: false,
    dateAsOf: (new Date()).toISOString().split("T")[0]
  }
  const [selItem, setSelItem] = useState<PartnersFeesTItemType>(initialItem)

  const handleOpenEdit = (val: PartnerFeesTEditDialog) => {
    setIsOpen(val)
  }
  const handleEdit = (val: PartnersFeesTItemType) => {
    setSelItem(val)
    setIsOpen(PartnerFeesTEditDialog.edit)
  }
  const handleCloseEdit = () => {
    setIsOpen(PartnerFeesTEditDialog.closed)
    setSelItem({...initialItem, partnerId: Number(selCustomer)})
  }
  const handleSubmitEdit = async (item: PartnersFeesTItemType, addMissingOnly: boolean = true) => {
    const isBulkAdd = isOpen === PartnerFeesTEditDialog.bulkAdd
    const qs = new URLSearchParams();
    qs.set("isBulkAdd", isBulkAdd ? "true" : "false");
    qs.set("addMissingOnly", addMissingOnly ? "true" : "false");

    await apiClient(`/PartnerTaxandFee/PartnersFeesT_SaveOrUpdate?${qs.toString()}`, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(({...item, id: item.partnersFeesTId})),
    }).then((responseJson) => {
      if (!responseJson) return
      Swal.fire({
        title: responseJson.message,
        text: "",
        icon: "success",
        confirmButtonText: "OK",
      })
    }).finally(() => {
      fetchPartnersFeesItem(selState, selCustomer, isLatest, filterModel, currentPage, pageSize, sortModel)
      handleCloseEdit()
    })
  }

  const columns: GridColDef[] = [
    {
      field: 'action', flex: 1, headerAlign: 'left', align: 'left', minWidth: 100, headerName: "Action", sortable: false, hideSortIcons: true, filterable: false,
      renderCell: (params) => (<div>

        {user?.userRoles.split(",").includes("Super Admin") ?
          <>
            <IconButton
              color="primary"
              onClick={() => { handleEdit(params.row) }}
              sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
            >
              <Tooltip
                title="Edit"
                placement="top"
                arrow
              >
                <EditOutlinedIcon fontSize="small" color="primary" />
              </Tooltip>
            </IconButton >
            <IconButton
              color="error"
              onClick={() => { handleDeleteItem(params.row.partnersFeesTId) }}
              sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(211,48,47,0.15) !important' } }}
            >
              <Tooltip
                title="Delete"
                placement="top"
                arrow
              >
                <DeleteIcon fontSize="small" color="error" />
              </Tooltip>
            </IconButton >
          </>
          : ""}
      </div>)
    },
    {
      field: 'homeState',
      headerName: 'Home State',
      flex: 1,
      type: 'string',
      minWidth: 140,
      headerAlign: 'left',
      align: 'left',
      filterOperators,
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'homeState'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'customerName',
      headerName: 'Client Name',
      flex: 1,
      type: 'string',
      minWidth: 180,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'customerName'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'zeroFee',
      headerName: 'Zero Fee',
      flex: 1,
      type: 'number',
      minWidth: 130,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'zeroFee'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'premiumFee',
      headerName: 'Premium Fee',
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      minWidth: 165,
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'premiumFee'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'policyFee',
      headerName: 'Policy Fee',
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      minWidth: 135,
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'policyFee'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'loydsFee',
      headerName: 'Loyds Fee',
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      minWidth: 140,
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'loydsFee'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'binderFee',
      headerName: 'Binder Fee',
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      minWidth: 140,
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'binderFee'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'endorsementFee',
      headerName: 'Endorsement Fee',
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      minWidth: 190,
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'endorsementFee'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'largeReportFee',
      headerName: 'Large Report Fee',
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      minWidth: 190,
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'largeReportFee'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'diligentSearchFee',
      headerName: 'Diligent Search Fee',
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      minWidth: 200,
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'diligentSearchFee'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
    {
      field: 'dateAsOf',
      headerName: 'Date As Of',
      flex: 1,
      type: 'string',
      headerAlign: 'left',
      align: 'left',
      minWidth: 150,
      hideSortIcons: true,
      filterOperators,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'dateAsOf'
          );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
            <div>
                <span className="table-card-title">{params.colDef.headerName}</span>
                {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
            </div>
        )},
    },
  ]

  const selectSx = {
    textTransform: "none" as const,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0", borderRadius: "6px" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2099d2" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2099d2", borderWidth: "1px" },
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100%", display: "flex", flexDirection: "column", backgroundColor: "#fafbfc" }}>
      <div style={{ position: "fixed", top: 0, left: 0, zIndex: 9990, width: "100%" }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
      </div>
      <Box
        sx={{
          display: "flex",
          // flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: { xs: 4, lg: 2 },
          py: 1.5,
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#fafbfc",
          minHeight: 56,
          mb: 0,
          marginLeft: { xs: '-24px', lg: '0px' },
          marginRight: { xs: '-24px', lg: '0px' },
        }}
      >
        <h4 className="mb-0 py-2" style={{ fontWeight: 600, color: "#0f172a" }}>
          Partner Fees T
        </h4>
        
        <Grid container className="w-auto" spacing={2} alignItems="center">
          <Grid item>
            <Button
              variant="contained"
              size="medium"
              onClick={() => handleOpenEdit(PartnerFeesTEditDialog.edit)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "#2099d2",
                "&:hover": { backgroundColor: "#176990" },
                px: 2,
              }}
            >
              Add Item
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              size="medium"
              onClick={() => handleOpenEdit(PartnerFeesTEditDialog.bulkAdd)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "#2099d2",
                "&:hover": { backgroundColor: "#176990" },
                px: 2,
              }}
            >
              Bulk Add
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          width: "100%",
          py: 2,
          px: 2,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Grid container spacing={2} alignItems="center" className="filter-grid" display="flex">
            <Grid item xl={2} lg={4} sm={6} xs={12}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  sx={{
                    // Parent/Container Style
                    marginTop: '-4px !important',
                    
                    // Target nested Input
                    '& .MuiAutocomplete-input': {
                      padding: '0px 4px 0px 5px !important',
                    },
                  }}
                  options={customerList}
                  getOptionLabel={(option) => option.customerName}
                  onChange={(event, value) => setSelCustomer(value?.partnersId.toString() ?? "")}
                  value={
                    (customerList.find((item) => item.partnersId.toString() === selCustomer) ??
                      null) as (typeof customerList)[0] | null
                  }
                  isOptionEqualToValue={(a, b) => a.partnersId === b.partnersId}
                  renderInput={(params) => {
                    return <TextField
                      sx={{
                        '.MuiFormLabel-root.Mui-focused': {
                          color: (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0) ? '#000' : '#cccccc !important'
                        },
                        '& .MuiInputBase-root': { marginTop: '5px !important' },
                        '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                        '& .MuiFormLabel-root': { transform: 'translate(14px, 13px) scale(1)' },
                        '& .MuiInputLabel-shrink': { transform: 'translate(14px, -2px) scale(0.75)' },
                      }}
                      {...params}
                      label="Client"
                      InputLabelProps={{ ...params.InputLabelProps, shrink: (parseInt(selCustomer) > 0 || (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0)) ? true : false }}
                    />
                  }}
                />
                {/* <InputLabel id="partner-fees-client-label">Client</InputLabel>
                <Select
                  labelId="partner-fees-client-label"
                  label="Client"
                  value={selCustomer}
                  onChange={handlePartnerChange}
                  IconComponent={ExpandMoreIcon}
                  sx={selectSx}
                >
                  <MenuItem value="">Select Client</MenuItem>
                  {customerList.map((customer) => (
                    <MenuItem key={customer.partnersId} value={String(customer.partnersId)}>
                      {customer.customerName}
                    </MenuItem>
                  ))}
                </Select> */}
              </FormControl>
            </Grid>
            <Grid item xl={2} lg={4} sm={6} xs={12}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  sx={{
                    // Parent/Container Style
                    marginTop: '-4px !important',
                    
                    // Target nested Input
                    '& .MuiAutocomplete-input': {
                      padding: '0px 4px 0px 5px !important',
                    },
                  }}
                  options={stateList}
                  getOptionLabel={(option) => option.stateName}
                  onChange={(event, value) => setSelState(value?.stateId.toString() ?? "")}
                  value={
                    (stateList.find((item) => item.stateId.toString() === selState) ??
                      null) as (typeof stateList)[0] | null
                  }
                  isOptionEqualToValue={(a, b) => a.stateId === b.stateId}
                  renderInput={(params) => {
                    return <TextField
                      sx={{
                        '.MuiFormLabel-root.Mui-focused': {
                          color: (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0) ? '#000' : '#cccccc !important'
                        },
                        '& .MuiInputBase-root': { marginTop: '5px !important' },
                        '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                        '& .MuiFormLabel-root': { transform: 'translate(14px, 13px) scale(1)' },
                        '& .MuiInputLabel-shrink': { transform: 'translate(14px, -2px) scale(0.75)' },
                      }}
                      {...params}
                      label="Home State"
                      InputLabelProps={{ ...params.InputLabelProps, shrink: (parseInt(selState) > 0 || (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0)) ? true : false }}
                    />
                  }}
                />
                {/* <Select
                  labelId="partner-fees-state-label"
                  label="Home State"
                  value={selState}
                  onChange={handleHomeStateChange}
                  IconComponent={ExpandMoreIcon}
                  sx={selectSx}
                >
                  <MenuItem value="">Home State</MenuItem>
                  {stateList.map((state) => (
                    <MenuItem key={state.stateId} value={String(state.stateId)}>
                      {state.stateName}
                    </MenuItem>
                  ))}
                </Select> */}
              </FormControl>
            </Grid>
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={isLatest}
                onChange={handleIsLatestChange}
                inputProps={{ "aria-label": "Available items only" }}
                color="primary"
                sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
              />
              <Typography sx={{ fontSize: "0.875rem", color: "#334155" }}>Available items</Typography>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<Close />}
                onClick={() => {
                  handleClearFilters();
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#e2e8f0",
                  color: "#475569",
                  "&:hover": {
                    borderColor: "#2099d2",
                    backgroundColor: "rgba(32, 153, 210, 0.04)",
                    color: "#2099d2",
                  },
                  px: 2,
                }}
              >
                Clear
              </Button>
            </Grid>
        </Grid>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", px: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            borderRadius: "12px",
            overflow: "auto",
            boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            height: 'calc(100vh - 300px)',
          }}
        >
          <DataGridPro
            getRowId={(r) => r.partnersFeesTId}
            onSortModelChange={(newSortModel: any) => {
              setSortModel(newSortModel ?? []);
              setCurrentPage(0);
            }}
            sortModel={sortModel}
            sortingMode="server"
            loading={loading}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={(newFilterModel) => {
              setFilterModel(newFilterModel);
              setCurrentPage(0);
            }}
            paginationMode="server"
            rowCount={totalRowCount}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={(newPage) => setCurrentPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }}
            rowsPerPageOptions={getRowsPerPageOptions(totalRowCount, pageSize)}
            rows={listData}
            columns={columns}
            pagination
            getRowClassName={(params) => {
              const rowIsLatest = Number(params.row.isLatest) === 1;
              return rowIsLatest && false ? "custom-row-bg-green" : "";
            }}
            sx={{
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              border: "none",
              fontSize: "14px",
              color: "#475569",
              backgroundColor: "#ffffff",
              // "& .MuiDataGridPro-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitle": {
              //   whiteSpace: "normal",
              //   lineHeight: 1.35,
              //   fontWeight: 600,
              //   fontSize: "12px",
              //   letterSpacing: "0.05em",
              //   textTransform: "uppercase",
              //   color: "#64748b",
              // },
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal",
                lineHeight: "normal",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f8fafc",
                borderBottom: "2px solid #e2e8f0",
                // borderTop: "2px solid #e2e8f0",
                minHeight: "52px !important",
                maxHeight: "52px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                "&:focus, &:focus-within": { outline: "none" },
              },
              "& .MuiDataGrid-row": {
                transition: "background-color 0.15s ease",
                "&:nth-of-type(even)": { backgroundColor: "#fafbfc" },
                "&:hover": { backgroundColor: "#f1f5f9 !important" },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f1f5f9",
                fontSize: "14px",
                color: "#334155",
                overflow: "visible",
                whiteSpace: "normal",
                lineHeight: "normal",
                wordWrap: "break-word",
                textOverflow: "clip",
                padding: "8px 16px",
                "&:focus": { outline: "none" },
              },
              "& .custom-row-bg-green .MuiDataGrid-cell": {
                backgroundColor: "#a2dda2",
              },
              "& .custom-row-bg-green:hover .MuiDataGrid-cell": { backgroundColor: '#81c781' },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                marginBottom: "0px !important",
              },
              "& .MuiDataGrid-virtualScroller": { border: "none" },
              "& .MuiDataGrid-main": { border: "none" },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
                minHeight: 56,
              },
              "& .MuiTablePagination-root": { color: "#64748b", fontSize: "13px" },
              "& .MuiTablePagination-select": {
                fontSize: "13px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
              },
              "& .MuiIconButton-root": {
                color: "#64748b",
                "&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
              },
            }}
          />
        </Box>
      </Box>
      <AddEditPartnersFeesT
        onClose={handleCloseEdit}
        isOpen={isOpen}
        initItem={selItem}
        selItem={selItem}
        onSubmit={handleSubmitEdit}
      />
    </Box>
  );
}
